"use client";

import * as React from "react";
import { formatInTimeZone, getTimezoneOffset } from "date-fns-tz";

import { cn } from "../../lib/utils";
import {
	formatTimezoneLabel,
	formatWallClock,
	getLocalTimezone,
	getTimezoneAbbreviation,
} from "../../lib/timezone";
import { Badge } from "./badge";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "./hover-card";

const TRIGGER_FORMAT = "dd MMM yyyy, h:mm a";

export type DateTimeZoneTooltipProps = {
	/** The absolute instant to display. */
	date: string | Date;
	/**
	 * IANA timezone used for the primary (visible) value and the purple badge row (e.g.
	 * `"Pacific/Auckland"`). Defaults to `"UTC"`.
	 */
	timezone?: string;
	/** Purple badge label for the display-zone row, e.g. `"Fund"`. Defaults to `"Selected"`. */
	label?: React.ReactNode;
	/** `date-fns` format used for the always-visible trigger wall clock. Defaults to `"dd MMM yyyy, h:mm a"`. */
	triggerFormat?: string;
	/** Whether the trigger and tooltip include the time. Set `false` for date-only values. Defaults to `true`. */
	showTime?: boolean;
	/** Extra classes for the trigger element. */
	className?: string;
	/** Replaces the trigger. Defaults to the wall clock in `timezone`. */
	children?: React.ReactNode;
	/** Note under the rows when the display and local offsets differ. No note by default. */
	differentZoneNote?: React.ReactNode;
};

const parseDate = (value: string | Date): Date | undefined => {
	const date = value instanceof Date ? value : new Date(value);
	return Number.isNaN(date.getTime()) ? undefined : date;
};

type ZoneRowProps = {
	badge: React.ReactNode;
	badgeVariant: "purple" | "success";
	wallClock: string;
	zoneLabel: string;
};

function ZoneRow({ badge, badgeVariant, wallClock, zoneLabel }: ZoneRowProps) {
	return (
		<div className="flex items-center gap-x-3 text-xs">
			<Badge
				variant={badgeVariant}
				className="h-5 w-16 shrink-0 justify-center"
			>
				{badge}
			</Badge>
			<span className="text-neutral-700 tabular-nums">{wallClock}</span>
			<span className="text-neutral-500">{zoneLabel}</span>
		</div>
	);
}

/**
 * Displays an absolute instant in the given `timezone` (with a short zone abbreviation). On hover it
 * opens a two-row comparison:
 *
 * ```
 * [Fund]   30 Jun 2026, 5:00 PM NZT (UTC+12:00)
 * [Local]  30 Jun 2026, 3:00 PM AET (UTC+10:00)
 * ```
 *
 * Date/time uses `text-neutral-700`; the zone abbreviation + offset uses `text-neutral-500`.
 * When the display and local offsets match, a note under the rows states that they are the same.
 * When they differ, `differentZoneNote` (if given) shows in that place.
 */
export function DateTimeZoneTooltip({
	date,
	timezone = "UTC",
	label = "Selected",
	triggerFormat = TRIGGER_FORMAT,
	showTime = true,
	className,
	children,
	differentZoneNote,
}: DateTimeZoneTooltipProps) {
	const parsed = parseDate(date);

	if (!parsed) {
		return <span className={className}>{String(date)}</span>;
	}

	const localTimezone = getLocalTimezone();
	const wallFormat = showTime ? triggerFormat : "dd MMM yyyy";
	const abbreviation = getTimezoneAbbreviation(parsed, timezone);
	const triggerWall = formatInTimeZone(parsed, timezone, wallFormat);
	const triggerLabel = abbreviation
		? `${triggerWall} ${abbreviation}`
		: triggerWall;

	const displayWall = formatWallClock(parsed, timezone, { showTime });
	const displayZone = formatTimezoneLabel(timezone, parsed);
	const localWall = formatWallClock(parsed, localTimezone, { showTime });
	const localZone = formatTimezoneLabel(localTimezone, parsed);
	const isSameZone =
		getTimezoneOffset(timezone, parsed) ===
		getTimezoneOffset(localTimezone, parsed);

	return (
		<HoverCard openDelay={150} closeDelay={100}>
			<HoverCardTrigger asChild>
				<span className={cn("cursor-default", className)}>
					{children ?? triggerLabel}
				</span>
			</HoverCardTrigger>
			<HoverCardContent align="start" side="bottom" className="w-auto p-3">
				<div className="grid gap-2">
					<ZoneRow
						badge={label}
						badgeVariant="purple"
						wallClock={displayWall}
						zoneLabel={displayZone}
					/>
					<ZoneRow
						badge="Local"
						badgeVariant="success"
						wallClock={localWall}
						zoneLabel={localZone}
					/>
					{isSameZone ? (
						<p className="text-[11px] text-neutral-500">
							Same as your local timezone
						</p>
					) : (
						differentZoneNote && (
							<p className="text-[11px] text-neutral-500">
								{differentZoneNote}
							</p>
						)
					)}
				</div>
			</HoverCardContent>
		</HoverCard>
	);
}
